import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  User, 
  Building2, 
  Save, 
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// EU countries list
const EU_COUNTRIES = [
  'Alemania', 'Austria', 'Bélgica', 'Bulgaria', 'Chipre', 'Croacia', 'Dinamarca',
  'Eslovaquia', 'Eslovenia', 'Estonia', 'Finlandia', 'Francia', 'Grecia', 'Hungría',
  'Irlanda', 'Italia', 'Letonia', 'Lituania', 'Luxemburgo', 'Malta', 'Países Bajos',
  'Polonia', 'Portugal', 'República Checa', 'Rumania', 'Suecia'
];

// Spanish provinces exempt from IVA
const EXEMPT_PROVINCES = ['Las Palmas', 'Santa Cruz de Tenerife', 'Ceuta', 'Melilla'];

// All Spanish provinces
const SPANISH_PROVINCES = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 'Badajoz', 'Barcelona',
  'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ceuta', 'Ciudad Real', 'Córdoba',
  'Cuenca', 'Girona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva', 'Huesca', 'Islas Baleares',
  'Jaén', 'La Coruña', 'La Rioja', 'Las Palmas', 'León', 'Lleida', 'Lugo', 'Madrid', 'Málaga',
  'Melilla', 'Murcia', 'Navarra', 'Ourense', 'Palencia', 'Pontevedra', 'Salamanca',
  'Santa Cruz de Tenerife', 'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo',
  'Valencia', 'Valladolid', 'Vizcaya', 'Zamora', 'Zaragoza'
];

const COUNTRIES = ['España', ...EU_COUNTRIES, 'Otro'];

const translations = {
  es: {
    title: 'Mi Cuenta',
    subtitle: 'Gestiona tu información personal y de facturación',
    back: 'Volver',
    particular: 'Particular',
    empresa: 'Empresa',
    // Particular fields
    fullName: 'Nombre y Apellidos',
    fullNamePlaceholder: 'Ej: Juan García López',
    dni: 'DNI / NIE',
    dniPlaceholder: 'Ej: 12345678A',
    street: 'Calle',
    streetPlaceholder: 'Ej: Calle Mayor',
    streetNumber: 'Número',
    streetNumberPlaceholder: 'Ej: 15, 2ºA',
    city: 'Ciudad',
    cityPlaceholder: 'Ej: Madrid',
    postalCode: 'Código Postal',
    postalCodePlaceholder: 'Ej: 28001',
    province: 'Provincia',
    provincePlaceholder: 'Selecciona provincia',
    country: 'País',
    countryPlaceholder: 'Selecciona país',
    email: 'Email',
    emailNote: 'El email no se puede modificar',
    phone: 'Teléfono',
    phonePlaceholder: 'Ej: 612345678',
    // Empresa fields
    companyName: 'Nombre de la Empresa',
    companyNamePlaceholder: 'Ej: Mi Empresa S.L.',
    cif: 'CIF / VAT',
    cifPlaceholder: 'Ej: B12345678 o EU123456789',
    companyStreet: 'Calle',
    companyStreetPlaceholder: 'Ej: Calle Mayor',
    companyStreetNumber: 'Número',
    companyStreetNumberPlaceholder: 'Ej: 15',
    companyCity: 'Ciudad',
    companyCityPlaceholder: 'Ej: Madrid',
    companyPostalCode: 'Código Postal',
    companyPostalCodePlaceholder: 'Ej: 28001',
    companyProvince: 'Provincia',
    companyCountry: 'País',
    // VAT info
    vatStatus: 'Estado IVA',
    vatExempt: 'Exento de IVA',
    vatApplies: 'IVA 21%',
    vatEU: 'Intracomunitario (0%)',
    validatingVat: 'Validando CIF...',
    vatValid: 'CIF válido',
    vatInvalid: 'CIF no válido',
    // Actions
    save: 'Guardar Cambios',
    saving: 'Guardando...',
    saved: '¡Datos guardados correctamente!',
    error: 'Error al guardar los datos',
    particularDesc: 'Datos personales para facturación como particular',
    empresaDesc: 'Datos de empresa para facturación',
    required: 'Obligatorio para realizar recargas',
  },
  en: {
    title: 'My Account',
    subtitle: 'Manage your personal and billing information',
    back: 'Back',
    particular: 'Individual',
    empresa: 'Company',
    fullName: 'Full Name',
    fullNamePlaceholder: 'E.g.: John Smith',
    dni: 'ID Number',
    dniPlaceholder: 'E.g.: 12345678A',
    street: 'Street',
    streetPlaceholder: 'E.g.: Main Street',
    streetNumber: 'Number',
    streetNumberPlaceholder: 'E.g.: 15, Apt 2A',
    city: 'City',
    cityPlaceholder: 'E.g.: London',
    postalCode: 'Postal Code',
    postalCodePlaceholder: 'E.g.: 28001',
    province: 'Province/State',
    provincePlaceholder: 'Select province',
    country: 'Country',
    countryPlaceholder: 'Select country',
    email: 'Email',
    emailNote: 'Email cannot be modified',
    phone: 'Phone',
    phonePlaceholder: 'E.g.: 612345678',
    companyName: 'Company Name',
    companyNamePlaceholder: 'E.g.: My Company Ltd.',
    cif: 'Tax ID / VAT',
    cifPlaceholder: 'E.g.: B12345678 or EU123456789',
    companyStreet: 'Street',
    companyStreetPlaceholder: 'E.g.: Main Street',
    companyStreetNumber: 'Number',
    companyStreetNumberPlaceholder: 'E.g.: 15',
    companyCity: 'City',
    companyCityPlaceholder: 'E.g.: London',
    companyPostalCode: 'Postal Code',
    companyPostalCodePlaceholder: 'E.g.: 28001',
    companyProvince: 'Province/State',
    companyCountry: 'Country',
    vatStatus: 'VAT Status',
    vatExempt: 'VAT Exempt',
    vatApplies: 'VAT 21%',
    vatEU: 'Intra-EU (0%)',
    validatingVat: 'Validating VAT...',
    vatValid: 'VAT valid',
    vatInvalid: 'VAT invalid',
    save: 'Save Changes',
    saving: 'Saving...',
    saved: 'Data saved successfully!',
    error: 'Error saving data',
    particularDesc: 'Personal data for individual billing',
    empresaDesc: 'Company data for business billing',
    required: 'Required for wallet recharges',
  },
  fr: {
    title: 'Mon Compte',
    subtitle: 'Gérez vos informations personnelles et de facturation',
    back: 'Retour',
    particular: 'Particulier',
    empresa: 'Entreprise',
    fullName: 'Nom et Prénom',
    fullNamePlaceholder: 'Ex: Jean Dupont',
    dni: 'Numéro d\'identité',
    dniPlaceholder: 'Ex: 12345678A',
    address: 'Adresse Complète',
    addressPlaceholder: 'Rue, numéro, code postal, ville',
    postalCode: 'Code Postal',
    postalCodePlaceholder: 'Ex: 75001',
    province: 'Province/Région',
    provincePlaceholder: 'Sélectionnez la province',
    country: 'Pays',
    countryPlaceholder: 'Sélectionnez le pays',
    email: 'Email',
    emailNote: 'L\'email ne peut pas être modifié',
    phone: 'Téléphone',
    phonePlaceholder: 'Ex: 612345678',
    companyName: 'Nom de l\'Entreprise',
    companyNamePlaceholder: 'Ex: Mon Entreprise SARL',
    cif: 'SIRET / TVA',
    cifPlaceholder: 'Ex: FR12345678901',
    fiscalAddress: 'Adresse Fiscale',
    fiscalAddressPlaceholder: 'Rue, numéro, code postal, ville',
    companyProvince: 'Province/Région',
    companyCountry: 'Pays',
    vatStatus: 'Statut TVA',
    vatExempt: 'Exonéré de TVA',
    vatApplies: 'TVA 21%',
    vatEU: 'Intracommunautaire (0%)',
    validatingVat: 'Validation du numéro TVA...',
    vatValid: 'TVA valide',
    vatInvalid: 'TVA invalide',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    saved: 'Données enregistrées avec succès!',
    error: 'Erreur lors de l\'enregistrement',
    particularDesc: 'Données personnelles pour la facturation individuelle',
    empresaDesc: 'Données d\'entreprise pour la facturation',
    required: 'Obligatoire pour les recharges',
  },
  de: {
    title: 'Mein Konto',
    subtitle: 'Verwalten Sie Ihre persönlichen und Rechnungsdaten',
    back: 'Zurück',
    particular: 'Privatperson',
    empresa: 'Unternehmen',
    fullName: 'Vollständiger Name',
    fullNamePlaceholder: 'Z.B.: Max Mustermann',
    dni: 'Ausweisnummer',
    dniPlaceholder: 'Z.B.: 12345678A',
    address: 'Vollständige Adresse',
    addressPlaceholder: 'Straße, Nummer, PLZ, Stadt',
    postalCode: 'Postleitzahl',
    postalCodePlaceholder: 'Z.B.: 10115',
    province: 'Bundesland',
    provincePlaceholder: 'Bundesland auswählen',
    country: 'Land',
    countryPlaceholder: 'Land auswählen',
    email: 'E-Mail',
    emailNote: 'E-Mail kann nicht geändert werden',
    phone: 'Telefon',
    phonePlaceholder: 'Z.B.: 612345678',
    companyName: 'Firmenname',
    companyNamePlaceholder: 'Z.B.: Meine Firma GmbH',
    cif: 'USt-IdNr.',
    cifPlaceholder: 'Z.B.: DE123456789',
    fiscalAddress: 'Rechnungsadresse',
    fiscalAddressPlaceholder: 'Straße, Nummer, PLZ, Stadt',
    companyProvince: 'Bundesland',
    companyCountry: 'Land',
    vatStatus: 'MwSt-Status',
    vatExempt: 'MwSt-befreit',
    vatApplies: 'MwSt 21%',
    vatEU: 'Innergemeinschaftlich (0%)',
    validatingVat: 'USt-IdNr. wird überprüft...',
    vatValid: 'USt-IdNr. gültig',
    vatInvalid: 'USt-IdNr. ungültig',
    save: 'Speichern',
    saving: 'Speichern...',
    saved: 'Daten erfolgreich gespeichert!',
    error: 'Fehler beim Speichern',
    particularDesc: 'Persönliche Daten für die Einzelabrechnung',
    empresaDesc: 'Unternehmensdaten für die Geschäftsabrechnung',
    required: 'Erforderlich für Aufladungen',
  },
  it: {
    title: 'Il Mio Account',
    subtitle: 'Gestisci le tue informazioni personali e di fatturazione',
    back: 'Indietro',
    particular: 'Privato',
    empresa: 'Azienda',
    fullName: 'Nome e Cognome',
    fullNamePlaceholder: 'Es: Mario Rossi',
    dni: 'Codice Fiscale',
    dniPlaceholder: 'Es: RSSMRA80A01H501U',
    address: 'Indirizzo Completo',
    addressPlaceholder: 'Via, numero, CAP, città',
    postalCode: 'CAP',
    postalCodePlaceholder: 'Es: 00100',
    province: 'Provincia',
    provincePlaceholder: 'Seleziona provincia',
    country: 'Paese',
    countryPlaceholder: 'Seleziona paese',
    email: 'Email',
    emailNote: 'L\'email non può essere modificata',
    phone: 'Telefono',
    phonePlaceholder: 'Es: 612345678',
    companyName: 'Nome Azienda',
    companyNamePlaceholder: 'Es: La Mia Azienda SRL',
    cif: 'Partita IVA',
    cifPlaceholder: 'Es: IT12345678901',
    fiscalAddress: 'Indirizzo Fiscale',
    fiscalAddressPlaceholder: 'Via, numero, CAP, città',
    companyProvince: 'Provincia',
    companyCountry: 'Paese',
    vatStatus: 'Stato IVA',
    vatExempt: 'Esente IVA',
    vatApplies: 'IVA 21%',
    vatEU: 'Intracomunitario (0%)',
    validatingVat: 'Verifica P.IVA...',
    vatValid: 'P.IVA valida',
    vatInvalid: 'P.IVA non valida',
    save: 'Salva',
    saving: 'Salvataggio...',
    saved: 'Dati salvati con successo!',
    error: 'Errore durante il salvataggio',
    particularDesc: 'Dati personali per la fatturazione individuale',
    empresaDesc: 'Dati aziendali per la fatturazione',
    required: 'Obbligatorio per le ricariche',
  },
  pt: {
    title: 'Minha Conta',
    subtitle: 'Gerencie suas informações pessoais e de faturamento',
    back: 'Voltar',
    particular: 'Particular',
    empresa: 'Empresa',
    fullName: 'Nome Completo',
    fullNamePlaceholder: 'Ex: João Silva',
    dni: 'CPF / RG',
    dniPlaceholder: 'Ex: 123.456.789-00',
    street: 'Rua',
    streetPlaceholder: 'Ex: Rua Principal',
    streetNumber: 'Número',
    streetNumberPlaceholder: 'Ex: 15, Apto 2A',
    city: 'Cidade',
    cityPlaceholder: 'Ex: São Paulo',
    postalCode: 'CEP',
    postalCodePlaceholder: 'Ex: 01310-100',
    province: 'Estado',
    provincePlaceholder: 'Selecione o estado',
    country: 'País',
    countryPlaceholder: 'Selecione o país',
    email: 'Email',
    emailNote: 'O email não pode ser modificado',
    phone: 'Telefone',
    phonePlaceholder: 'Ex: 11999999999',
    companyName: 'Nome da Empresa',
    companyNamePlaceholder: 'Ex: Minha Empresa Ltda',
    cif: 'CNPJ / VAT',
    cifPlaceholder: 'Ex: 12.345.678/0001-00',
    companyStreet: 'Rua',
    companyStreetPlaceholder: 'Ex: Rua Principal',
    companyStreetNumber: 'Número',
    companyStreetNumberPlaceholder: 'Ex: 15',
    companyCity: 'Cidade',
    companyCityPlaceholder: 'Ex: São Paulo',
    companyPostalCode: 'CEP',
    companyPostalCodePlaceholder: 'Ex: 01310-100',
    companyProvince: 'Estado',
    companyCountry: 'País',
    vatStatus: 'Status IVA',
    vatExempt: 'Isento de IVA',
    vatApplies: 'IVA 21%',
    vatEU: 'Intracomunitário (0%)',
    validatingVat: 'Validando CNPJ...',
    vatValid: 'CNPJ válido',
    vatInvalid: 'CNPJ inválido',
    save: 'Salvar Alterações',
    saving: 'Salvando...',
    saved: 'Dados salvos com sucesso!',
    error: 'Erro ao salvar os dados',
    particularDesc: 'Dados pessoais para faturamento individual',
    empresaDesc: 'Dados da empresa para faturamento',
    required: 'Obrigatório para recargas',
  },
};

const MyAccountPage = () => {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const t = translations[language] || translations.es;
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('particular');
  const [validatingVat, setValidatingVat] = useState(false);
  const [vatValid, setVatValid] = useState(null);
  const [vatValidationResult, setVatValidationResult] = useState(null);
  
  // Particular data
  const [particularData, setParticularData] = useState({
    full_name: '',
    dni: '',
    street: '',
    street_number: '',
    city: '',
    postal_code: '',
    province: '',
    country: 'España',
    phone: '',
  });
  
  // Empresa data
  const [empresaData, setEmpresaData] = useState({
    company_name: '',
    cif: '',
    company_street: '',
    company_street_number: '',
    company_city: '',
    company_postal_code: '',
    company_province: '',
    company_country: 'España',
  });

  // Fetch profile data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const profileRes = await fetch(`${API_URL}/api/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (profileRes.ok) {
          const data = await profileRes.json();
          setParticularData({
            full_name: data.full_name || '',
            dni: data.dni || '',
            street: data.street || '',
            street_number: data.street_number || '',
            city: data.city || '',
            postal_code: data.postal_code || '',
            province: data.province || '',
            country: data.country || 'España',
            phone: data.phone || '',
          });
          setEmpresaData({
            company_name: data.company_name || '',
            cif: data.cif || data.cif_nif || '',
            company_street: data.company_street || '',
            company_street_number: data.company_street_number || '',
            company_city: data.company_city || '',
            company_postal_code: data.company_postal_code || '',
            company_province: data.company_province || '',
            company_country: data.company_country || 'España',
          });
          
          // Determine active tab based on saved billing_type
          if (data.billing_type === 'empresa') {
            setActiveTab('empresa');
          } else {
            setActiveTab('particular');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Calculate VAT rate based on location
  const calculateVatRate = () => {
    if (activeTab === 'empresa') {
      const country = empresaData.company_country;
      const province = empresaData.company_province;
      const cif = empresaData.cif?.toUpperCase() || '';
      
      // Spain
      if (country === 'España') {
        // Canarias, Ceuta, Melilla - exempt
        if (EXEMPT_PROVINCES.includes(province)) {
          return { rate: 0, reason: 'exempt_territory' };
        }
        // Peninsular Spain - 21%
        return { rate: 21, reason: 'spain' };
      }
      
      // EU country with valid VAT
      if (EU_COUNTRIES.includes(country)) {
        // Check if CIF starts with EU country code (intra-community)
        const euPrefixes = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'FI', 'FR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'];
        const hasEuPrefix = euPrefixes.some(prefix => cif.startsWith(prefix));
        
        if (hasEuPrefix && vatValid) {
          return { rate: 0, reason: 'eu_vat' };
        }
        return { rate: 0, reason: 'eu_pending_validation' };
      }
      
      // Rest of the world - exempt
      return { rate: 0, reason: 'non_eu' };
    } else {
      // Particular
      const country = particularData.country;
      const province = particularData.province;
      
      if (country === 'España') {
        if (EXEMPT_PROVINCES.includes(province)) {
          return { rate: 0, reason: 'exempt_territory' };
        }
        return { rate: 21, reason: 'spain' };
      }
      
      if (EU_COUNTRIES.includes(country)) {
        return { rate: 21, reason: 'eu_individual' };
      }
      
      return { rate: 0, reason: 'non_eu' };
    }
  };

  const vatInfo = calculateVatRate();

  // Validate EU VAT number
  const validateVatNumber = async (vatNumber) => {
    if (!vatNumber || vatNumber.length < 5) {
      setVatValid(null);
      setVatValidationResult(null);
      return;
    }
    
    setValidatingVat(true);
    try {
      // Use the VIES validation endpoint
      const response = await fetch(`${API_URL}/api/vies/validate/${encodeURIComponent(vatNumber)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setVatValid(data.valid);
        setVatValidationResult(data);
      } else {
        setVatValid(null);
        setVatValidationResult(null);
      }
    } catch (error) {
      console.error('Error validating VAT:', error);
      setVatValid(null);
      setVatValidationResult(null);
    } finally {
      setValidatingVat(false);
    }
  };

  // Debounce VAT validation
  useEffect(() => {
    const timer = setTimeout(() => {
      // Validate if country is Spain or any other EU country
      const isEuCountry = empresaData.company_country === 'España' || EU_COUNTRIES.includes(empresaData.company_country);
      if (empresaData.cif && isEuCountry) {
        validateVatNumber(empresaData.cif);
      } else {
        setVatValid(null);
        setVatValidationResult(null);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [empresaData.cif, empresaData.company_country]);

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...particularData,
          // Build full address from parts for backwards compatibility
          address: `${particularData.street} ${particularData.street_number}, ${particularData.city}`.trim(),
          company_name: empresaData.company_name,
          cif: empresaData.cif,
          cif_nif: empresaData.cif,
          company_street: empresaData.company_street,
          company_street_number: empresaData.company_street_number,
          company_city: empresaData.company_city,
          fiscal_address: `${empresaData.company_street} ${empresaData.company_street_number}, ${empresaData.company_city}`.trim(),
          company_address: `${empresaData.company_street} ${empresaData.company_street_number}, ${empresaData.company_city}`.trim(),
          company_postal_code: empresaData.company_postal_code,
          company_province: empresaData.company_province,
          company_country: empresaData.company_country,
          billing_type: activeTab,
          vat_rate: vatInfo.rate,
          vat_reason: vatInfo.reason,
        }),
      });
      
      if (response.ok) {
        toast({
          title: t.saved,
          variant: 'default',
        });
        // Redirect to catalog after saving
        setTimeout(() => {
          navigate('/catalog');
        }, 1000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: t.error,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const getVatStatusBadge = () => {
    if (vatInfo.rate === 0) {
      if (vatInfo.reason === 'eu_vat') {
        return <Badge className="bg-blue-100 text-blue-700">{t.vatEU}</Badge>;
      }
      return <Badge className="bg-green-100 text-green-700">{t.vatExempt}</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700">{t.vatApplies}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <User className="h-6 w-6 text-primary" />
              {t.title}
            </h1>
            <p className="text-muted-foreground text-sm">{t.subtitle}</p>
          </div>
        </div>

        {/* VAT Status Info */}
        <Card className="mb-6 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t.vatStatus}:</span>
                {getVatStatusBadge()}
              </div>
              <p className="text-xs text-muted-foreground">{t.required}</p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="particular" className="flex items-center gap-2" data-testid="tab-particular">
              <User className="h-4 w-4" />
              {t.particular}
            </TabsTrigger>
            <TabsTrigger value="empresa" className="flex items-center gap-2" data-testid="tab-empresa">
              <Building2 className="h-4 w-4" />
              {t.empresa}
            </TabsTrigger>
          </TabsList>

          {/* Particular Tab */}
          <TabsContent value="particular">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  {t.particular}
                </CardTitle>
                <CardDescription>{t.particularDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">{t.fullName} <span className="text-red-500">*</span></Label>
                    <Input
                      id="fullName"
                      data-testid="input-fullname"
                      value={particularData.full_name}
                      onChange={(e) => setParticularData({ ...particularData, full_name: e.target.value })}
                      placeholder={t.fullNamePlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dni">{t.dni} <span className="text-red-500">*</span></Label>
                    <Input
                      id="dni"
                      data-testid="input-dni"
                      value={particularData.dni}
                      onChange={(e) => setParticularData({ ...particularData, dni: e.target.value })}
                      placeholder={t.dniPlaceholder}
                    />
                  </div>
                </div>
                
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="street">{t.street} <span className="text-red-500">*</span></Label>
                    <Input
                      id="street"
                      data-testid="input-street"
                      value={particularData.street}
                      onChange={(e) => setParticularData({ ...particularData, street: e.target.value })}
                      placeholder={t.streetPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="streetNumber">{t.streetNumber} <span className="text-red-500">*</span></Label>
                    <Input
                      id="streetNumber"
                      data-testid="input-street-number"
                      value={particularData.street_number}
                      onChange={(e) => setParticularData({ ...particularData, street_number: e.target.value })}
                      placeholder={t.streetNumberPlaceholder}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">{t.city} <span className="text-red-500">*</span></Label>
                    <Input
                      id="city"
                      data-testid="input-city"
                      value={particularData.city}
                      onChange={(e) => setParticularData({ ...particularData, city: e.target.value })}
                      placeholder={t.cityPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">{t.postalCode} <span className="text-red-500">*</span></Label>
                    <Input
                      id="postalCode"
                      data-testid="input-postal-code"
                      value={particularData.postal_code}
                      onChange={(e) => setParticularData({ ...particularData, postal_code: e.target.value })}
                      placeholder={t.postalCodePlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.country} <span className="text-red-500">*</span></Label>
                    <Select
                      value={particularData.country}
                      onValueChange={(value) => setParticularData({ ...particularData, country: value, province: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.countryPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t.province} {particularData.country === 'España' && <span className="text-red-500">*</span>}</Label>
                    {particularData.country === 'España' ? (
                      <Select
                        value={particularData.province}
                        onValueChange={(value) => setParticularData({ ...particularData, province: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.provincePlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {SPANISH_PROVINCES.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={particularData.province}
                        onChange={(e) => setParticularData({ ...particularData, province: e.target.value })}
                        placeholder={t.provincePlaceholder}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t.phone}</Label>
                    <Input
                      id="phone"
                      data-testid="input-phone"
                      value={particularData.phone}
                      onChange={(e) => setParticularData({ ...particularData, phone: e.target.value })}
                      placeholder={t.phonePlaceholder}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">{t.email}</Label>
                  <Input
                    id="email"
                    data-testid="input-email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{t.emailNote}</p>
                </div>

                {/* Save Button for Particular */}
                <div className="pt-4 flex justify-end">
                  <Button onClick={handleSave} disabled={saving} data-testid="save-particular-btn">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.saving}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {t.save}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Empresa Tab */}
          <TabsContent value="empresa">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  {t.empresa}
                </CardTitle>
                <CardDescription>{t.empresaDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t.companyName} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-company-name"
                      value={empresaData.company_name}
                      onChange={(e) => setEmpresaData({ ...empresaData, company_name: e.target.value })}
                      placeholder={t.companyNamePlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.cif} <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Input
                        data-testid="input-cif"
                        value={empresaData.cif}
                        onChange={(e) => setEmpresaData({ ...empresaData, cif: e.target.value.toUpperCase() })}
                        placeholder={t.cifPlaceholder}
                        className={vatValid === false ? 'border-red-500' : vatValid === true ? 'border-green-500' : ''}
                      />
                      {validatingVat && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      {!validatingVat && vatValid === true && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        </div>
                      )}
                      {!validatingVat && vatValid === false && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        </div>
                      )}
                    </div>
                    {validatingVat && <p className="text-xs text-muted-foreground">{t.validatingVat}</p>}
                    {!validatingVat && vatValid === true && (
                      <div className="text-xs text-green-600 space-y-0.5">
                        <p className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {t.vatValid}
                        </p>
                        {vatValidationResult?.company_name && (
                          <p className="text-muted-foreground pl-4">{vatValidationResult.company_name}</p>
                        )}
                      </div>
                    )}
                    {!validatingVat && vatValid === false && (
                      <div className="text-xs text-red-600">
                        <p className="flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {vatValidationResult?.error || t.vatInvalid}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>{t.companyStreet || t.street} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-company-street"
                      value={empresaData.company_street}
                      onChange={(e) => setEmpresaData({ ...empresaData, company_street: e.target.value })}
                      placeholder={t.companyStreetPlaceholder || t.streetPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.companyStreetNumber || t.streetNumber} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-company-street-number"
                      value={empresaData.company_street_number}
                      onChange={(e) => setEmpresaData({ ...empresaData, company_street_number: e.target.value })}
                      placeholder={t.companyStreetNumberPlaceholder || t.streetNumberPlaceholder}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>{t.companyCity || t.city} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-company-city"
                      value={empresaData.company_city}
                      onChange={(e) => setEmpresaData({ ...empresaData, company_city: e.target.value })}
                      placeholder={t.companyCityPlaceholder || t.cityPlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.companyPostalCode || t.postalCode} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="input-company-postal-code"
                      value={empresaData.company_postal_code}
                      onChange={(e) => setEmpresaData({ ...empresaData, company_postal_code: e.target.value })}
                      placeholder={t.companyPostalCodePlaceholder || t.postalCodePlaceholder}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t.companyCountry} <span className="text-red-500">*</span></Label>
                    <Select
                      value={empresaData.company_country}
                      onValueChange={(value) => {
                        setEmpresaData({ ...empresaData, company_country: value, company_province: '' });
                        setVatValid(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.countryPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t.companyProvince} {empresaData.company_country === 'España' && <span className="text-red-500">*</span>}</Label>
                    {empresaData.company_country === 'España' ? (
                      <Select
                        value={empresaData.company_province}
                        onValueChange={(value) => setEmpresaData({ ...empresaData, company_province: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t.provincePlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {SPANISH_PROVINCES.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={empresaData.company_province}
                        onChange={(e) => setEmpresaData({ ...empresaData, company_province: e.target.value })}
                        placeholder={t.provincePlaceholder}
                      />
                    )}
                  </div>
                </div>

                {/* Save Button for Empresa */}
                <div className="pt-4 flex justify-end">
                  <Button onClick={handleSave} disabled={saving} data-testid="save-empresa-btn">
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.saving}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {t.save}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MyAccountPage;
